'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';

type LoginFields = { email: string; password: string; remember: boolean };
type SignupFields = { name: string; restaurantName: string; email: string; password: string; terms: boolean };

export default function AuthForm() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const loginForm = useForm<LoginFields>({ mode: 'onBlur' });
  const signupForm = useForm<SignupFields>({ mode: 'onBlur' });

  const handleLogin = async (data: LoginFields) => {
    setIsLoading(true);
    // Backend integration point: Supabase auth.signInWithPassword({ email: data.email, password: data.password })
    await new Promise((r) => setTimeout(r, 1200));
    setIsLoading(false);
    if (data.email === 'owner@theamberkitchen.co.uk' && data.password === 'demo1234') {
      toast.success('Welcome back, Marco!');
      router.push('/reply-history-dashboard');
    } else {
      toast.error('Incorrect email or password. Try owner@theamberkitchen.co.uk / demo1234');
    }
  };

  const handleSignup = async (data: SignupFields) => {
    setIsLoading(true);
    // Backend integration point: Supabase auth.signUp({ email: data.email, password: data.password, options: { data: { name, restaurantName } } })
    await new Promise((r) => setTimeout(r, 1400));
    setIsLoading(false);
    toast.success('Account created! Check your email to verify your address.');
    setMode('login');
  };

  return (
    <div className="flex-1 flex items-center justify-center p-8 lg:p-12" style={{ backgroundColor: 'var(--background)' }}>
      <div className="w-full max-w-md">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} style={{ color: '#FDFAF6' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
          </div>
          <span className="font-serif text-lg" style={{ color: 'var(--foreground)' }}>ReviewReply AI</span>
        </div>

        {/* Mode toggle */}
        <div
          className="flex p-1 rounded-xl mb-8"
          style={{ backgroundColor: 'var(--muted)' }}
        >
          {(['login', 'signup'] as const).map((m) => (
            <button
              key={`mode-${m}`}
              onClick={() => setMode(m)}
              className="flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200"
              style={{
                backgroundColor: mode === m ? 'var(--card)' : 'transparent',
                color: mode === m ? 'var(--foreground)' : 'var(--muted-foreground)',
                boxShadow: mode === m ? '0 1px 4px rgba(26,10,2,0.08)' : 'none',
              }}
            >
              {m === 'login' ? 'Sign in' : 'Create account'}
            </button>
          ))}
        </div>

        {mode === 'login' ? (
          <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5" noValidate>
            <div>
              <p className="font-serif text-2xl mb-1" style={{ color: 'var(--foreground)' }}>Welcome back</p>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Sign in to your ReviewReply account</p>
            </div>

            <div>
              <label className="form-label" htmlFor="login-email">Email address</label>
              <input
                id="login-email"
                type="email"
                className="form-input"
                placeholder="you@restaurant.com"
                autoComplete="email"
                {...loginForm.register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
              />
              {loginForm.formState.errors.email && (
                <p className="form-error">{loginForm.formState.errors.email.message}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="form-label" htmlFor="login-password" style={{ marginBottom: 0 }}>Password</label>
                <button type="button" className="text-xs" style={{ color: 'var(--primary)' }}>
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input pr-10"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  {...loginForm.register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-150"
                  style={{ color: 'var(--muted-foreground)' }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
              {loginForm.formState.errors.password && (
                <p className="form-error">{loginForm.formState.errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <input
                id="remember"
                type="checkbox"
                className="w-4 h-4 rounded accent-primary cursor-pointer"
                {...loginForm.register('remember')}
              />
              <label htmlFor="remember" className="text-sm cursor-pointer" style={{ color: 'var(--muted-foreground)' }}>
                Keep me signed in for 30 days
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in…
                </>
              ) : 'Sign in to your account'}
            </button>

            <p className="text-center text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Don&apos;t have an account?{' '}
              <button
                type="button"
                onClick={() => setMode('signup')}
                className="font-semibold"
                style={{ color: 'var(--primary)' }}
              >
                Create one free
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-5" noValidate>
            <div>
              <p className="font-serif text-2xl mb-1" style={{ color: 'var(--foreground)' }}>Create your account</p>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Start with 5 free replies — no card required</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label" htmlFor="signup-name">Your name</label>
                <input
                  id="signup-name"
                  type="text"
                  className="form-input"
                  placeholder="Marco"
                  autoComplete="given-name"
                  {...signupForm.register('name', { required: 'Name is required' })}
                />
                {signupForm.formState.errors.name && (
                  <p className="form-error">{signupForm.formState.errors.name.message}</p>
                )}
              </div>
              <div>
                <label className="form-label" htmlFor="signup-restaurant">Restaurant name</label>
                <input
                  id="signup-restaurant"
                  type="text"
                  className="form-input"
                  placeholder="The Amber Kitchen"
                  {...signupForm.register('restaurantName', { required: 'Restaurant name is required' })}
                />
                {signupForm.formState.errors.restaurantName && (
                  <p className="form-error">{signupForm.formState.errors.restaurantName.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="form-label" htmlFor="signup-email">Email address</label>
              <input
                id="signup-email"
                type="email"
                className="form-input"
                placeholder="you@restaurant.com"
                autoComplete="email"
                {...signupForm.register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
              />
              {signupForm.formState.errors.email && (
                <p className="form-error">{signupForm.formState.errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="form-label" htmlFor="signup-password">Password</label>
              <div className="relative">
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input pr-10"
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  {...signupForm.register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Password must be at least 8 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--muted-foreground)' }}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
              {signupForm.formState.errors.password && (
                <p className="form-error">{signupForm.formState.errors.password.message}</p>
              )}
            </div>

            <div className="flex items-start gap-2">
              <input
                id="terms"
                type="checkbox"
                className="w-4 h-4 mt-0.5 rounded accent-primary cursor-pointer"
                {...signupForm.register('terms', { required: 'You must accept the terms to continue' })}
              />
              <label htmlFor="terms" className="text-xs cursor-pointer leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                I agree to ReviewReply AI&apos;s{' '}
                <a href="#" className="underline" style={{ color: 'var(--primary)' }}>Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="underline" style={{ color: 'var(--primary)' }}>Privacy Policy</a>
              </label>
            </div>
            {signupForm.formState.errors.terms && (
              <p className="form-error -mt-3">{signupForm.formState.errors.terms.message}</p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating your account…
                </>
              ) : 'Create free account'}
            </button>

            <p className="text-center text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => setMode('login')}
                className="font-semibold"
                style={{ color: 'var(--primary)' }}
              >
                Sign in
              </button>
            </p>
          </form>
        )}

        {/* Back to home */}
        <div className="mt-8 text-center">
          <Link href="/" className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            ← Back to ReviewReply AI
          </Link>
        </div>
      </div>
    </div>
  );
}